import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Typography,
} from "@mui/material";
import { COUNTRY, FAIRFAX, LIBERSEGUROS, PORTAL_NAME } from "config";
import { FunctionComponent } from "react";
import { useSelector } from "react-redux";

import { customStylesSelector } from "store/selectors/selectors";
import { useT } from "translation";
import {
  CountryToLanguageMapping,
  CurrencyPrefix,
  formatCoverageValue,
  formatCurrency,
} from "utils/utils";

interface CoveragesProps {
  selectedQuote?: Quote;
  showHeading?: boolean;
}

const Coverages: FunctionComponent<CoveragesProps> = ({
  selectedQuote,
  showHeading,
}) => {
  const { t } = useT();
  const customStyles = useSelector(customStylesSelector);

  const AccordionSummaryTitle = () => {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "5px",
        }}
      >
        <Typography
          sx={{
            fontSize: "16px",
            fontWeight: 500,
            color: customStyles.muiTheme.palette.text.secondary,
          }}
        >
          {t("QUOTE_PAGE_COVERAGES")}
        </Typography>
        {!selectedQuote && (
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 500,
              color: customStyles.muiTheme.palette.text.secondary,
            }}
          >
            {t("QUOTE_PAGE_SELECT_ONE_PLAN")}
          </Typography>
        )}
      </Box>
    );
  };

  const coveragePremiumBreakdown = selectedQuote?.premium?.coverage_info || {};

  const Details = selectedQuote?.coverage?.sections?.map((section) => (
    <Accordion
      defaultExpanded
      key={section?.title?.default}
      sx={{
        borderRadius: "12px !important",
        boxShadow: "none",
        border: "none",
        "&.MuiAccordion-root:before": {
          backgroundColor: "transparent",
        },
        "&.Mui-expanded": {
          margin: "0px",
        },
        backgroundColor: customStyles.primaryCard.backgroundColor,
      }}
    >
      <AccordionSummary
        expandIcon={
          <ExpandMoreIcon
            style={{
              color: customStyles.muiTheme.palette.text.secondary,
            }}
          />
        }
        sx={{
          padding: "0px 16px",
          margin: "0px",
          minHeight: "0px",
          ".MuiAccordionSummary-content": {
            margin: "0px",
            color: customStyles.muiTheme.palette.text.secondary,
          },
          "&.Mui-expanded": {
            minHeight: "0px",
          },
        }}
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        <Box
          sx={{
            display: "flex",
            gap: "5px",
            flexDirection: "column",
          }}
        >
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 600,
              color: customStyles.muiTheme.palette.text.secondary,
            }}
          >
            {section.title?.[CountryToLanguageMapping[COUNTRY]] ??
              section.title?.default}
          </Typography>
          {section?.description?.default && (
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                color: customStyles.muiTheme.palette.text.tertiary,
              }}
            >
              {section.description?.[CountryToLanguageMapping[COUNTRY]] ??
                section.description?.default}
            </Typography>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {(section?.children ?? []).map((ch, index) => (
          <Box
            key={index}
            sx={{
              mt: "20px",
              ml: "10px",
              display: "flex",
              gap: "5px",
              flexDirection: "column",
            }}
          >
            <Typography
              sx={{
                fontSize: "15px",
                fontWeight: 600,
                color: customStyles.muiTheme.palette.text.secondary,
              }}
            >
              {ch?.label?.[CountryToLanguageMapping[COUNTRY]] ??
                ch.label?.default}
            </Typography>
            <Divider />
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                color: customStyles.muiTheme.palette.text.tertiary,
              }}
            >
              {ch?.details?.[CountryToLanguageMapping[COUNTRY]] ??
                ch.details?.default}
            </Typography>
            {/* If a coverage field has a limit. Some products have limit and some don't */}
            {ch.limit && (
              <Box sx={{ display: "flex", gap: "5px" }}>
                {/* TODO: Need to analyse this for all products */}
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: customStyles.muiTheme.palette.text.secondary,
                  }}
                >
                  {t("QUOTE_PAGE_COVERAGE_LIMIT")}
                </Typography>

                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: customStyles.muiTheme.palette.text.tertiary,
                  }}
                >
                  {formatCoverageValue(ch.limit)}
                </Typography>
                {PORTAL_NAME === FAIRFAX &&
                  coveragePremiumBreakdown[ch.code] !== 0 && (
                    <>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 500,
                          color: customStyles.muiTheme.palette.text.secondary,
                        }}
                      >
                        {COUNTRY === "br" ? "Prêmio:" : "Premium:"}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 500,
                          color: customStyles.muiTheme.palette.text.tertiary,
                        }}
                      >
                        {`${CurrencyPrefix} ${formatCurrency(
                          coveragePremiumBreakdown[ch.code]
                        )}`}
                      </Typography>
                    </>
                  )}
              </Box>
            )}
            {ch.limits && (
              <Box sx={{ display: "flex", gap: "5px" }}>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: customStyles.muiTheme.palette.text.secondary,
                  }}
                >
                  {t("QUOTE_PAGE_COVERAGE_LIMIT")}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    gap: "5px",
                    flexDirection: "column",
                  }}
                >
                  {ch.limits.map((limit) => (
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 500,
                        color: customStyles.muiTheme.palette.text.tertiary,
                      }}
                    >
                      {formatCoverageValue(limit)}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}

            {/* If a coverage field has deductible */}
            {ch.deductible && (
              <Box sx={{ display: "flex", gap: "5px" }}>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: customStyles.muiTheme.palette.text.secondary,
                  }}
                >
                  {t("QUOTE_PAGE_COVERAGE_DEDUCTIBLE")}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: customStyles.muiTheme.palette.text.tertiary,
                  }}
                >
                  {formatCoverageValue(ch.deductible)}
                </Typography>
              </Box>
            )}
          </Box>
        ))}
      </AccordionDetails>
    </Accordion>
  ));

  const TotalAnnualCoverage = () => {
    if (!selectedQuote?.premium?.additional_info?.total_coverage) return null;
    return (
      <Box
        sx={{
          px: "20px",
          display: "flex",
          flexDirection: "column",
          gap: "5px",
          mb: "20px",
        }}
      >
        <Typography
          sx={{
            fontSize: "16px",
            fontWeight: 600,
            color: customStyles.muiTheme.palette.text.secondary,
          }}
        >
          {COUNTRY === "br"
            ? "Cobertura Total Anual"
            : COUNTRY === "mx"
            ? "Cobertura Anual Total"
            : "Total Annual Coverage"}
        </Typography>
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 600,
            color: customStyles.muiTheme.palette.text.tertiary,
          }}
        >
          {selectedQuote?.premium?.additional_info?.total_coverage}
        </Typography>
      </Box>
    );
  };

  if (!showHeading) {
    return (
      <>
        {/* <TotalAnnualCoverage /> */}
        {Details}
      </>
    );
  }

  return (
    <Accordion
      defaultExpanded
      sx={{
        width: "100%",
        borderRadius: "12px !important",
        boxShadow: "none",
        border: "none",
        "&.MuiAccordion-root:before": {
          backgroundColor: "transparent",
        },
        backgroundColor: customStyles.primaryCard.backgroundColor,
      }}
      disabled={!selectedQuote}
    >
      <AccordionSummary
        expandIcon={
          <ExpandMoreIcon
            style={{
              color: customStyles.muiTheme.palette.text.secondary,
            }}
          />
        }
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        <AccordionSummaryTitle />
      </AccordionSummary>
      <AccordionDetails sx={{ gap: "30px" }}>
        {/* <TotalAnnualCoverage /> */}
        {Details}
      </AccordionDetails>
    </Accordion>
  );
};

Coverages.defaultProps = { showHeading: true };

export default Coverages;
